<template>
  <div ref="groupRoot" class="sp-info-card-group" :class="classModifiers" role="list">
    <slot />
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { useBreakpoints } from "../../composables/breakpoints";
import { useColor } from "../../composables/color";
import { toBoolean, toCssUnit } from "../../utils/props";
import { getSlotContentByTagName } from "../../utils/slots";

const props = defineProps({
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "neutral", "inverted"].includes(value),
  },
  itemsPerRow: {
    type: [Number, String],
    default: "auto",
    validator: (value) => ["auto", "1", "2", "3", "4"].includes(String(value)),
  },
  connectOnSm: {
    type: [Boolean, String],
    default: false,
  },
  equalizeHeights: {
    type: [Boolean, String],
    default: false,
  },
  minHeight: {
    type: [String, Number],
    default: 0,
  },
  minHeightSm: {
    type: [String, Number],
    default: 0,
  },
  color: {
    type: String,
    default: "primary",
    validator: (value) =>
      [
        "primary",
        "secondary",
        "tertiary",
        "danger",
        "text",
        "error",
        "warning",
        "success",
        "info",
        "white",
        "custom",
      ].includes(value),
  },
});

const { isSmScreen } = useBreakpoints();
const { colorModifiers } = useColor(props);

const groupRoot = ref(null);
const cardElements = ref([]);
const equalizedHeight = ref(0);

const isConnectedMode = computed(() => toBoolean(props.connectOnSm) && isSmScreen.value);
const isEqualize = computed(() => toBoolean(props.equalizeHeights));
const isFixedItemsPerRow = computed(() => props.itemsPerRow !== "auto" && !isConnectedMode.value && !isSmScreen.value);

const effectiveMinHeight = computed(() => toCssUnit(props.minHeight));
const effectiveMinHeightSm = computed(() => toCssUnit(props.minHeightSm));

const classModifiers = computed(() => [
  ...colorModifiers.value,
  { "--connected": isConnectedMode.value },
  { "--is-sm": isSmScreen.value },
  {
    [`--items-per-row-${props.itemsPerRow}`]: isFixedItemsPerRow.value,
  },
  { [`--variant-${props.variant}`]: true },
  { "--equalize": isEqualize.value && !isConnectedMode.value },
]);

function updateChildrenContext() {
  cardElements.value.forEach((cardEl) => {
    cardEl.setAttribute("role", "listitem");
  });
}

async function findMaxEqualizedHeight() {
  equalizedHeight.value = 0;
  await nextTick();
  const heights = cardElements.value.map((el) => el.getBoundingClientRect?.().height || el.offsetHeight || 0);
  const max = Math.max(...heights, 0);
  equalizedHeight.value = `${Math.round(max)}px`;
}

function equalizeOrResetHeights() {
  if (isEqualize.value && !isConnectedMode.value) {
    findMaxEqualizedHeight();
  } else {
    equalizedHeight.value = "0px";
  }
}

watch(groupRoot, () => {
  cardElements.value = getSlotContentByTagName(groupRoot.value, "SP-INFO-CARD");
});

watch(
  [cardElements, isConnectedMode, isEqualize, isSmScreen],
  async () => {
    await nextTick();
    updateChildrenContext();
    equalizeOrResetHeights();
  },
  { immediate: true, flush: "post" },
);
</script>

<style lang="scss">
@import "./shared/info-card-theme";

:host {
  /* Layout and sizing */
  --gap: var(--sp-comp-info-card-group-gap, var(--sp-ref-spacing-12));
  --gap-sm: var(--sp-comp-info-card-group-gap-sm, var(--sp-ref-spacing-8));
  --card-min-width: var(--sp-comp-info-card-min-width, 15rem);

  /* Connected */
  --border-radius: var(--sp-comp-info-card-group-border-radius, var(--sp-ref-border-radius-lg));
  --background: var(--sp-comp-info-card-group-background, var(--sp-sys-color-surface));
  --box-shadow: var(--sp-comp-info-card-group-box-shadow, var(--sp-sys-elevation-3));

  --padding-block: var(--sp-comp-info-card-group-padding-block, var(--sp-ref-spacing-4));
  --padding-inline: var(--sp-comp-info-card-group-padding-inline, var(--sp-ref-spacing-6));
  --padding-block-item: var(--sp-comp-info-card-group-padding-block-item, var(--sp-ref-spacing-6));
  --padding-inline-item: var(--sp-comp-info-card-group-padding-inline-item, var(--sp-ref-spacing-4));
  --divider-color: var(--sp-comp-info-card-group-connected-divider-color, var(--sp-sys-color-outline-variant));
  --divider-inset: var(--sp-comp-info-card-group-divider-inset, 0);

  display: block;
}
</style>

<style scoped lang="scss">
.sp-info-card-group {
  display: grid;
  gap: var(--gap);
  grid-template-columns: repeat(auto-fill, minmax(var(--card-min-width), 1fr));
  justify-items: stretch;
  align-items: start;

  &:not(.--connected).--equalize {
    ::slotted(sp-info-card) {
      --sp-info-card-group-min-height: v-bind(equalizedHeight);
    }
  }

  &:not(.--connected):not(.--equalize) {
    ::slotted(sp-info-card) {
      --sp-info-card-group-min-height: v-bind(effectiveMinHeight);
    }
  }

  &.--is-sm {
    gap: var(--gap-sm);
    grid-template-columns: 1fr;

    &:not(.--connected).--equalize {
      ::slotted(sp-info-card) {
        --sp-info-card-group-min-height-sm: v-bind(equalizedHeight);
      }
    }

    &:not(.--connected):not(.--equalize) {
      ::slotted(sp-info-card) {
        --sp-info-card-group-min-height-sm: v-bind(effectiveMinHeightSm);
      }
    }
  }

  @for $i from 1 through 4 {
    &.--items-per-row-#{$i} {
      grid-template-columns: repeat(#{$i}, 1fr);
    }
  }

  @each $v in default, neutral, inverted {
    &.--variant-#{$v} {
      --gap: var(--#{$v}-group-gap);
      --gap-sm: var(--#{$v}-group-gap-sm);
    }
  }

  &.--connected {
    display: block;
    padding-block: var(--padding-block);
    padding-inline: var(--padding-inline);
    border-radius: var(--border-radius);
    background: var(--background);
    box-shadow: var(--box-shadow);
    overflow: hidden;

    @each $v in default, neutral, inverted {
      &.--variant-#{$v} {
        --border-radius: var(--#{$v}-border-radius);
        --background: var(--#{$v}-background);
        --divider-color: var(--#{$v}-divider-color);
      }
    }

    @each $c in primary, secondary, danger, text, error, warning, success, info, "white", custom {
      &.--variant-inverted.--color-#{$c} {
        --background: var(--sp-sys-color-#{$c});
        --divider-color: var(--sp-ref-palette-#{$c}-darken-1, var(--sp-sys-color-on-#{$c}));
      }
    }

    ::slotted(sp-info-card) {
      --sp-info-card-group-header-direction: row;
      --sp-info-card-group-header-align: center;
      --sp-info-card-group-text-align: left;

      --sp-info-card-group-padding-block: var(--padding-block-item);
      --sp-info-card-group-padding-inline: var(--padding-inline-item);
      --sp-info-card-group-padding-block-sm: var(--padding-block-item);
      --sp-info-card-group-padding-inline-sm: var(--padding-inline-item);

      --sp-info-card-group-border-radius: 0;
      --box-shadow: none;
    }

    ::slotted(sp-info-card:not(:first-child)) {
      &::before {
        content: "";
        display: block;
        height: 1px;
        background-color: var(--divider-color);
        margin-inline: var(--divider-inset, 0);
      }
    }
  }
}
</style>
